import { useState } from "react";
import { Sparkles, AlertTriangle, Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const EXAMPLES = [
  "add +15875551234 as UCaaS, carrier Bell",
  "add +15875550100 through 0149 as UCaaS",
  "change +15875551234 carrier status to Suspended",
  "delete +15875551234",
];

export const AiDidCommand = ({ open, onOpenChange, onDone }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [bulkConflict, setBulkConflict] = useState(null);

  const reset = () => { setText(""); setProposal(null); setLoading(false); setExecuting(false); setBulkConflict(null); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const interpret = async () => {
    if (!text.trim()) { toast.error("Type a command first"); return; }
    setLoading(true); setProposal(null); setBulkConflict(null);
    try {
      const { data } = await api.post("/assistant/did-command", { command: text.trim() });
      setProposal(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not interpret that");
    } finally { setLoading(false); }
  };

  const execute = async () => {
    if (!proposal) return;
    setExecuting(true); setBulkConflict(null);
    try {
      if (proposal.action === "add") {
        await api.post("/dids", proposal.payload);
        toast.success(`Added ${proposal.payload.number}`);
      } else if (proposal.action === "bulk_add") {
        const { data } = await api.post("/dids/bulk", { numbers: proposal.numbers, ...proposal.fields });
        toast.success(`Added ${data.added} DIDs`);
      } else if (proposal.action === "update") {
        await api.put(`/dids/${proposal.did_id}`, proposal.payload);
        toast.success(`Updated ${proposal.number}`);
      } else if (proposal.action === "delete") {
        await api.delete(`/dids/${proposal.did_id}`);
        toast.success(`Deleted ${proposal.number}`);
      }
      reset(); onOpenChange(false); onDone?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (proposal.action === "bulk_add" && e.response?.status === 409 && detail?.conflicts) {
        setBulkConflict(detail.conflicts);
      } else {
        toast.error(formatApiErrorDetail(detail) || "Action failed (check your permissions)");
      }
    } finally { setExecuting(false); }
  };

  const isDelete = proposal?.action === "delete";
  const isBulk = proposal?.action === "bulk_add";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="ai-did-command-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" /> Number inventory — AI command</DialogTitle>
          <DialogDescription>Add, change or delete a DID in plain English. You'll confirm before anything changes.</DialogDescription>
        </DialogHeader>

        <Textarea data-testid="ai-did-input" rows={2} value={text} autoFocus
          onChange={(e) => { setText(e.target.value); setProposal(null); }}
          placeholder="e.g. add +15875551234 as UCaaS" />
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLES.map((ex) => (
            <button key={ex} onClick={() => { setText(ex); setProposal(null); }}
              className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground">{ex}</button>
          ))}
        </div>

        {proposal && proposal.action === "clarify" && (
          <div data-testid="ai-did-clarify" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            <AlertTriangle className="mb-1 h-4 w-4" /> {proposal.message}
          </div>
        )}

        {proposal && proposal.action !== "clarify" && (
          <div data-testid="ai-did-proposal" className={`rounded-lg border p-3 text-sm ${isDelete ? "border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/40" : "border-border bg-muted/40"}`}>
            <div className="mb-2 flex items-center gap-1.5 font-semibold text-foreground">
              {proposal.action === "add" && <><Plus className="h-4 w-4 text-green-600" /> Add DID</>}
              {isBulk && <><Plus className="h-4 w-4 text-green-600" /> Bulk add {proposal.count} DIDs</>}
              {proposal.action === "update" && <><Pencil className="h-4 w-4 text-sky-600" /> Update DID</>}
              {isDelete && <><Trash2 className="h-4 w-4 text-rose-600" /> Delete DID</>}
            </div>
            {proposal.action === "add" && (
              <ul className="space-y-0.5 text-muted-foreground">
                {Object.entries(proposal.payload).map(([k, v]) => (
                  <li key={k}><span className="font-medium text-foreground">{k}</span>: {String(v)}</li>
                ))}
              </ul>
            )}
            {proposal.action === "update" && (
              <>
                <p className="mb-1 text-foreground">Number <span className="font-bold">{proposal.number}</span>:</p>
                <ul className="space-y-0.5 text-muted-foreground">
                  {Object.entries(proposal.changes || {}).map(([k, c]) => (
                    <li key={k}><span className="font-medium text-foreground">{k}</span>: {String(c.from ?? "—")} → <span className="font-semibold text-sky-700 dark:text-sky-300">{String(c.to)}</span></li>
                  ))}
                </ul>
              </>
            )}
            {isDelete && (
              <p className="text-rose-700 dark:text-rose-300">This will permanently delete <span className="font-bold">{proposal.number}</span> from the number inventory. This cannot be undone.</p>
            )}
            {isBulk && (
              <div className="space-y-1 text-muted-foreground">
                <p>Range: <span className="font-medium text-foreground">{proposal.range?.start}</span> → <span className="font-medium text-foreground">{proposal.range?.end}</span> · <span className="font-semibold text-foreground">{proposal.count}</span> numbers</p>
                <ul className="space-y-0.5">
                  {Object.entries(proposal.fields || {}).map(([k, v]) => (
                    <li key={k}><span className="font-medium text-foreground">{k}</span>: {String(v)}</li>
                  ))}
                </ul>
                <p className="text-xs">Preview: {(proposal.preview || []).join(", ")}{proposal.count > (proposal.preview || []).length ? " …" : ""}</p>
              </div>
            )}
          </div>
        )}

        {bulkConflict && (
          <div data-testid="ai-did-bulk-conflict" className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
            <div className="mb-1 flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" /> Bulk add cancelled — nothing was added</div>
            <p>These numbers already exist, so the whole batch was stopped to avoid duplicates:</p>
            <p className="mt-1 font-mono text-xs font-medium">{bulkConflict.slice(0, 12).join(", ")}{bulkConflict.length > 12 ? ` +${bulkConflict.length - 12} more` : ""}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={loading || executing}>Cancel</Button>
          {!proposal || proposal.action === "clarify" ? (
            <Button data-testid="ai-did-interpret" onClick={interpret} disabled={loading} className="bg-violet-600 hover:bg-violet-700 text-white">
              {loading ? "Thinking…" : <><Sparkles className="h-4 w-4" /> Interpret</>}
            </Button>
          ) : (
            <Button data-testid="ai-did-confirm" onClick={execute} disabled={executing}
              className={isDelete ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-primary hover:bg-primary/90 text-primary-foreground"}>
              {executing ? "Working…" : isDelete ? "Delete it" : isBulk ? `Add ${proposal.count} DIDs` : "Confirm"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
